"use client";
import { FaPlus, FaArrowRight, FaMapMarkerAlt } from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import { GOOGLE_GEOCODING_API_KEY } from "../config";
import AddressAutocomplete from "./AddressAutocomplete";

const OrderFormStep1 = ({
  handleStep1Submit,
  address,
  handleAddressChange,
  storedAddresses,
  product,
  normalizeString,
  state,
  pincode,
  setAddress,
  setState,
  setPincode,
  setSelectedCode,
  setSelectedSiteBalance,
  setAccountDetails,
  showNewSiteForm,
  setShowNewSiteForm,
  handleSiteSelection,
  statesList,
  handleStateChange,
  handlePincodeChange,
  selectedGrade,
  handleGradeSelection,
  selectedMt,
  handleMtChange,
  mt,
  selectedQuantity,
  handleQuantityChange,
  quantities,
  customQuantity,
  setCustomQuantity,
  isStep1Complete,
}) => {
  const [locating, setLocating] = useState(false);

  // Use Current Location – works in both flows
  const handleUseCurrentLocation = async () => {
    if (locating) return;
    setLocating(true);

    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
        });
      });

      const { latitude, longitude } = pos.coords;
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_GEOCODING_API_KEY}&language=en`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.status !== "OK" || !data.results?.[0]) throw new Error("No address");

      const r = data.results[0];
      const c = r.address_components;
      const get = (t) => c.find((x) => x.types.includes(t))?.long_name ?? "";

      setAddress(r.formatted_address.split(",")[0].trim());
      setState(get("administrative_area_level_1"));
      setPincode(get("postal_code") || "");
      setSelectedCode(null);
      setSelectedSiteBalance(0);
      setAccountDetails(null);
    } catch (err) {
      alert("Could not detect location. Please type manually.");
    } finally {
      setLocating(false);
    }
  };

  const hasSavedSites = storedAddresses.filter((a) => a.brand === product.name).length > 0;

  return (
    <form onSubmit={handleStep1Submit}>
      {/* SITE ADDRESS */}
      <div className="site-address-field">
        <div className="orderForm-group full-width">
          <label className="form-label-static">Site Address:</label>

          {hasSavedSites ? (
            <>
              <div className="existing-sites-container">
                {storedAddresses
                  .filter((a) => a.brand === product.name)
                  .map((a, i) => (
                    <div key={i} className="site-option">
                      <input
                        type="checkbox"
                        id={`site-${i}`}
                        checked={
                          normalizeString(address) === normalizeString(a.address) &&
                          normalizeString(state) === normalizeString(a.state) &&
                          normalizeString(pincode) === normalizeString(a.pincode)
                        }
                        onChange={() => {
                          if (
                            address === a.address &&
                            state === a.state &&
                            pincode === a.pincode
                          ) {
                            setAddress("");
                            setState("");
                            setPincode("");
                            setSelectedCode(null);
                            setSelectedSiteBalance(0);
                            setAccountDetails(null);
                          } else {
                            setShowNewSiteForm(false);
                            handleSiteSelection(a);
                          }
                        }}
                        className="site-checkbox"
                      />
                      <label htmlFor={`site-${i}`} className="site-label">
                        {a.address}, {a.state}, {a.pincode}
                      </label>
                    </div>
                  ))}
              </div>

              <button
                type="button"
                className="add-new-site-btn"
                onClick={() => {
                  setAddress("");
                  setState("");
                  setPincode("");
                  setSelectedCode(null);
                  setSelectedSiteBalance(0);
                  setAccountDetails(null);
                  setShowNewSiteForm(true);
                }}
              >
                <FaPlus className="add-icon" /> Add New Site
              </button>
            </>
          ) : (
            /* NO SAVED SITES → Show exact same form as "Add New Site" */
            <div
              style={{
                padding: "16px",
                border: "1px dashed #ccc",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                marginTop: "8px",
              }}
            >
              <div className="orderForm-group full-width">
                <label>New Site Address:</label>
                <AddressAutocomplete
                  address={address}
                  setAddress={setAddress}
                  setState={setState}
                  setPincode={setPincode}
                />
              </div>

              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={locating}
                style={{
                  margin: "12px 0",
                  background: locating ? "#ccc" : "#ea8716",
                  color: "white",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <FaMapMarkerAlt />
                {locating ? "Detecting location…" : "Use My Current Location"}
              </button>

              <div className="orderForm-row">
                <div className="orderForm-group">
                  <label>State:</label>
                  <select
                    value={state}
                    onChange={handleStateChange}
                    className="product-form-select"
                  >
                    <option value="">Auto-detected</option>
                    {statesList.map((s, i) => (
                      <option key={i} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="orderForm-group">
                  <label>Pincode:</label>
                  <input
                    type="text"
                    value={pincode}
                    onChange={handlePincodeChange}
                    maxLength={6}
                    placeholder="Auto-filled"
                    className="product-form-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* NEW SITE FORM (when user clicks "Add New Site" from saved sites) */}
      {showNewSiteForm && (
        <div
          className="new-site-form"
          style={{
            marginTop: "16px",
            padding: "16px",
            border: "1px dashed #ccc",
            borderRadius: "8px",
          }}
        >
          <div className="orderForm-group full-width">
            <label>New Site Address:</label>
            <AddressAutocomplete
              address={address}
              setAddress={setAddress}
              setState={setState}
              setPincode={setPincode}
            />
          </div>

          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={locating}
            style={{
              margin: "8px 0",
              background: locating ? "#ccc" : "#ea8716",
              color: "white",
              border: "none",
              padding: "6px 12px",
              borderRadius: "4px",
            }}
          >
            <FaMapMarkerAlt /> {locating ? "Detecting…" : "Use Current Location"}
          </button>

          <div className="orderForm-row">
            <div className="orderForm-group">
              <label>State:</label>
              <select
                value={state}
                onChange={handleStateChange}
                required
                className="product-form-select"
              >
                <option value="">Select State</option>
                {statesList.map((s, i) => (
                  <option key={i} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="orderForm-group">
              <label>Pincode:</label>
              <input
                type="text"
                value={pincode}
                onChange={handlePincodeChange}
                maxLength={6}
                className="product-form-input"
              />
            </div>
          </div>

          <button
            type="button"
            className="cancel-new-site"
            onClick={() => setShowNewSiteForm(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {/* REST OF THE FORM (unchanged) */}
      <div className="orderForm-group full-width">
        <label className="form-label-static">Grade Selection:</label>
        <div className="product-button-group">
          {["OPC43", "OPC53", "PPC", "PSC"].map((g) => (
            <button
              type="button"
              key={g}
              className={`option-btn ${selectedGrade === g ? "selected" : ""}`}
              onClick={() => handleGradeSelection(g)}
            >
              {g}
            </button>
          ))}
        </div>

        <div className="orderForm-row">
          <div className="orderForm-group full-width">
            <label className="form-label-static">Quantity Selection:</label>

            <div className="quantity-selection-tabs">
              <button
                type="button"
                className={`tab-btn ${!selectedMt ? "active" : ""}`}
                onClick={() => {
                  // Switch to Bags: clear MT, keep bags (or clear if you want)
                  handleMtChange({ target: { value: "" } });
                  setCustomQuantity("");
                }}
              >
                Bags
              </button>

              {/* MT (Loose) TAB */}
              <button
                type="button"
                className={`tab-btn ${selectedMt ? "active" : ""}`}
                onClick={() => {
                  // Switch to MT: clear bags
                  handleQuantityChange({ target: { value: "" } });
                  setCustomQuantity("");

                  // IMPORTANT: Set selectedMt to first option so MT dropdown appears
                  if (mt.length > 0) {
                    handleMtChange({ target: { value: mt[0] } }); // e.g. "25 MT"
                  }
                }}
              >
                MT(Loose)
              </button>
            </div>

            {/* BAGS SECTION */}
            {!selectedMt && (
              <div className="quantity-row">
                <select
                  value={selectedQuantity}
                  onChange={handleQuantityChange}
                  required={!selectedMt}
                  className="product-form-select"
                >
                  <option value="">Select Bags</option>
                  {quantities.map((q, i) => (
                    <option key={i} value={q}>{q} </option>
                  ))}
                </select>

                {selectedQuantity === "Other Qty" && (
                  <input
                    type="number"
                    value={customQuantity}
                    onChange={(e) => setCustomQuantity((e.target.value))}
                    placeholder="Enter other quantity"
                    min="240"
                    className="product-form-input"
                    onWheel={(e) => e.target.blur()}
                  />
                )}
              </div>
            )}

            {/* MT SECTION */}
            {selectedMt && (
              <select
                value={selectedMt}
                onChange={handleMtChange}
                required={!!selectedMt}
                className="product-form-select"
              >
                <option value="">Select MT</option>
                {mt.map((v, i) => (
                  <option key={i} value={v}>{v}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <button
          type="submit"
          className={`action-btn next-step ${isStep1Complete ? "active" : ""}`}
          disabled={!isStep1Complete}
        >
          Rate Request <FaArrowRight className="ml-2" />
        </button>
      </div>
    </form>
  );
};

export default OrderFormStep1;